/**
 * Cómo se NOMBRA una unidad dentro de una etiqueta.
 *
 * Existe porque las etiquetas de los formularios de presentaciones hablaban de «pieza» sin decir de
 * qué: con una «Caja de 9 Bolsas», «Contenido por pieza» deja la duda de si la pieza es la caja o la
 * bolsa. Nombrar la unidad elegida —«Litros por empaque»— quita la duda de raíz.
 *
 * El plural se calcula, no se pide, porque las unidades las crea el propio negocio y nadie va a
 * escribirle un plural a cada una.
 */

/** Vocales, con y sin acento: deciden si el plural es «+s» o «+es». */
const TERMINA_EN_VOCAL = /[aeiouáéíóú]$/i;
/** Una palabra que no acaba en letra (un código, un id de prueba) no se pluraliza: no es una palabra. */
const TERMINA_EN_LETRA = /[a-záéíóúñü]$/i;

const SIN_ACENTO: Record<string, string> = { á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u' };

/**
 * Plural español de un nombre de unidad. Las reglas que hacen falta aquí y ninguna más:
 *  · termina en vocal  → +s      (Gramo → Gramos, Pieza → Piezas)
 *  · termina en consonante → +es (Galón → Galones)
 *  · y al añadir «es», la última sílaba pierde el acento, porque deja de ser aguda:
 *    Galón lleva tilde por ser aguda acabada en n; Galones ya no lo es.
 */
export function pluralizeUnitName(name: string): string {
  const limpio = name.trim();
  if (!limpio) return limpio;
  if (TERMINA_EN_VOCAL.test(limpio)) return `${limpio}s`;
  if (!TERMINA_EN_LETRA.test(limpio)) return limpio;
  // Quita la tilde de la ÚLTIMA vocal acentuada: es la que la pierde al dejar de ser aguda.
  const sinTilde = limpio.replace(/[áéíóú](?=[^áéíóú]*$)/i, (vocal) => {
    const minuscula = SIN_ACENTO[vocal.toLowerCase()] ?? vocal;
    return vocal === vocal.toUpperCase() ? minuscula.toUpperCase() : minuscula;
  });
  return `${sinTilde}es`;
}

/**
 * El texto de una unidad para una etiqueta, en plural y en minúscula: «gramos», «litros».
 *
 * Cae al SÍMBOLO cuando el nombre no es una palabra —una unidad importada con un código por nombre—,
 * porque «Ajena-1788817s» no es el plural de nada. El símbolo nunca necesita inflexión.
 */
/**
 * El singular, para «cuánto sale cada litro». Sale del NOMBRE tal cual y no de quitarle la «s» al
 * plural: Galón → Galones → «galon» perdía el acento, porque el plural ya lo había quitado.
 */
export function unitWordSingular(unit: { name: string; symbol: string } | undefined | null): string {
  if (!unit) return 'unidad';
  const nombre = unit.name.trim();
  if (!nombre || !TERMINA_EN_LETRA.test(nombre)) return unit.symbol.trim() || 'unidad';
  return nombre.toLowerCase();
}

export function unitWordPlural(unit: { name: string; symbol: string } | undefined | null): string {
  if (!unit) return 'unidades';
  const nombre = unit.name.trim();
  if (!nombre || !TERMINA_EN_LETRA.test(nombre)) return unit.symbol.trim() || 'unidades';
  return pluralizeUnitName(nombre).toLowerCase();
}
